import { abrePDF } from "../../utils/pdf";
import React, { useContext, useMemo, useState } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import {
  Button,
  Row,
  Col,
  Table,
  Input,
  Label,
  ButtonGroup,
} from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import moment from "moment";
import { TableHeader, Pagination, Search } from "../../DataTable";

function ListadoCobranzaVendedor() {
  const { user } = useContext(AuthContext);
  const URL_DASHBOARDS = process.env.REACT_APP_URL_DASHBOARDS;
  const URL_LOGOS = process.env.REACT_APP_URL_LOGOS;

  const [data, setData] = useState([]);
  const [selectedVendedor, setSelectedVendedor] = useState("");
  const [selectedCliente, setSelectedCliente] = useState("");
  const [selectedEstado, setSelectedEstado] = useState(""); // "" | "vencidas" | "noVencidas"
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sorting, setSorting] = useState({ field: "", order: "" });
  const ITEMS_PER_PAGE = 50;

  const RANGOS = ["enTiempo", "treinta", "sesenta", "noventa", "cientoveinte", "cientoochenta", "otro"];
  const NOMBRE_RANGO = {
    enTiempo: "En tiempo",
    treinta: "1-30 días",
    sesenta: "31-60 días",
    noventa: "61-90 días",
    cientoveinte: "91-120 días",
    cientoochenta: "121-180 días",
    otro: "+180 días",
  };

  useMemo(() => {
    axios
      .get(`${URL_DASHBOARDS}AntiguedadSaldosVencido`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((res) => {
        const filas = [];
        const hoy = moment().startOf("day");
        (res.data || []).forEach((cli) => {
          for (const rango of RANGOS) {
            const surtidos = cli.surtidos?.[rango] || [];
            const notas = cli.notasCargo?.[rango] || [];
            for (const s of surtidos) {
              const venc = s.fechaVencimiento || "";
              // negativo = días que faltan por vencer, positivo = días vencidos
              const dias = venc ? hoy.diff(moment(venc), "days") : 0;
              filas.push({
                _id: s._id,
                tipo: "Venta",
                numero: s.idSurtido || "",
                cliente: cli.nombreCliente,
                clienteId: cli.clienteId,
                vendedorId: cli.vendedorId || "",
                vendedorNombre: cli.vendedorNombre || "Sin vendedor",
                fecha: s.fecha,
                fechaVencimiento: venc,
                diasVencido: dias,
                total: Number(s.total_general || 0),
                saldo: Number(s.saldo || 0),
                rango,
                rangoNombre: NOMBRE_RANGO[rango],
              });
            }
            for (const n of notas) {
              const venc = n.fechaVencimiento || "";
              // negativo = días que faltan por vencer, positivo = días vencidos
              const dias = venc ? hoy.diff(moment(venc), "days") : 0;
              filas.push({
                _id: n._id,
                tipo: "Nota Cargo",
                numero: n.idNotaCargo || "",
                cliente: cli.nombreCliente,
                clienteId: cli.clienteId,
                vendedorId: cli.vendedorId || "",
                vendedorNombre: cli.vendedorNombre || "Sin vendedor",
                fecha: n.fecha,
                fechaVencimiento: venc,
                diasVencido: dias,
                total: Number(n.total || n.total_general || 0),
                saldo: Number(n.saldo || 0),
                rango,
                rangoNombre: NOMBRE_RANGO[rango],
              });
            }
          }
        });
        setData(filas);
      })
      .catch((err) => console.log(err));
  }, []);

  const vendedores = useMemo(() => {
    const map = new Map();
    for (const f of data) if (f.vendedorId) map.set(f.vendedorId, f.vendedorNombre);
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [data]);

  const clientesFiltro = useMemo(() => {
    const map = new Map();
    for (const f of data) {
      if (selectedVendedor && f.vendedorId !== selectedVendedor) continue;
      if (f.clienteId) map.set(f.clienteId, f.cliente);
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [data, selectedVendedor]);

  const filtradas = useMemo(() => {
    let arr = data.slice();
    if (selectedVendedor) arr = arr.filter((f) => f.vendedorId === selectedVendedor);
    if (selectedCliente) arr = arr.filter((f) => f.clienteId === selectedCliente);
    if (selectedEstado === "vencidas") {
      arr = arr.filter((f) => Number(f.diasVencido) > 0);
    } else if (selectedEstado === "noVencidas") {
      arr = arr.filter((f) => Number(f.diasVencido) <= 0);
    }
    if (search) {
      const s = search.toLowerCase();
      arr = arr.filter(
        (f) =>
          (f.cliente || "").toLowerCase().includes(s) ||
          String(f.numero).includes(s) ||
          (f.vendedorNombre || "").toLowerCase().includes(s)
      );
    }
    arr.sort((a, b) => (a.fechaVencimiento || "").localeCompare(b.fechaVencimiento || ""));
    if (sorting.field) {
      const dir = sorting.order === "asc" ? 1 : -1;
      arr.sort((a, b) => {
        const va = a[sorting.field];
        const vb = b[sorting.field];
        if (typeof va === "number") return (va - vb) * dir;
        return String(va || "").localeCompare(String(vb || "")) * dir;
      });
    }
    return arr;
  }, [data, selectedVendedor, selectedCliente, selectedEstado, search, sorting]);

  const pagina = filtradas.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalSaldo = filtradas.reduce((s, f) => s + Number(f.saldo || 0), 0);
  const totalTotal = filtradas.reduce((s, f) => s + Number(f.total || 0), 0);
  // Vencido = saldo de las notas con días vencidos > 0 (mismo criterio que el filtro)
  const totalVencido = filtradas.reduce(
    (s, f) => s + (Number(f.diasVencido) > 0 ? Number(f.saldo || 0) : 0),
    0
  );
  const notasVencidas = filtradas.filter((f) => Number(f.diasVencido) > 0).length;
  const fmt = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(n) || 0);

  const headers = [
    { name: "Vendedor", field: "vendedorNombre", sortable: true },
    { name: "Cliente", field: "cliente", sortable: true },
    { name: "Tipo", field: "tipo", sortable: true },
    { name: "Numero", field: "numero", sortable: true },
    { name: "Fecha", field: "fecha", sortable: true },
    { name: "Fecha Venc.", field: "fechaVencimiento", sortable: true },
    { name: "Dias Vencido", field: "diasVencido", sortable: true },
    { name: "Rango", field: "rangoNombre", sortable: true },
    { name: "Total", field: "total", sortable: true },
    { name: "Saldo", field: "saldo", sortable: true },
  ];

  // Etiquetas para reflejar los filtros activos en título/nombre de archivo
  const vendedorLabel = selectedVendedor
    ? vendedores.find((v) => v[0] === selectedVendedor)?.[1] || "Todos"
    : "Todos";
  const clienteLabel = selectedCliente
    ? clientesFiltro.find((c) => c[0] === selectedCliente)?.[1] || ""
    : "";
  const estadoLabel =
    selectedEstado === "vencidas"
      ? "Vencidas"
      : selectedEstado === "noVencidas"
      ? "No vencidas"
      : "Todas";
  const slug = (s) => String(s || "").replace(/[^\w]/g, "_");

  function PDF() {
    const doc = new jsPDF("landscape", "mm", "a4", true);
    doc.setFontSize(11);
    doc.text(`Cobranza por Vendedor — ${vendedorLabel}`, 14, 12);
    doc.setFontSize(9);
    const filtrosLinea = [
      clienteLabel ? `Cliente: ${clienteLabel}` : null,
      `Estado: ${estadoLabel}`,
      search ? `Búsqueda: "${search}"` : null,
    ].filter(Boolean).join("  ·  ");
    doc.text(filtrosLinea, 14, 18);
    doc.setFontSize(10);
    doc.text(
      `Total pendiente: ${fmt(totalSaldo)}   ·   Notas: ${filtradas.length}`,
      14,
      24
    );
    // El vencido en rojo, que es el número que importa para cobranza
    doc.setTextColor(200, 30, 30);
    doc.text(
      `Total vencido: ${fmt(totalVencido)}   ·   Notas vencidas: ${notasVencidas}`,
      14,
      29.5
    );
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);

    const body = filtradas.map((f) => [
      f.vendedorNombre,
      f.cliente,
      f.tipo,
      f.numero,
      f.fecha,
      f.fechaVencimiento,
      f.diasVencido,
      f.rangoNombre,
      fmt(f.total),
      fmt(f.saldo),
    ]);
    doc.autoTable({
      head: [["Vendedor", "Cliente", "Tipo", "N°", "Fecha", "Fecha Venc.", "Dias", "Rango", "Total", "Saldo"]],
      body,
      startY: 34,
      theme: "grid",
      headStyles: { fillColor: [200, 30, 30], textColor: 255, fontSize: 8, halign: "center" },
      bodyStyles: { fontSize: 7 },
      foot: [[
        "", "", "", "", "", "", "", "TOTALES",
        fmt(totalTotal),
        fmt(totalSaldo),
      ]],
      footStyles: { fillColor: [200, 30, 30], textColor: 255, fontSize: 8, halign: "right" },
      columnStyles: { 8: { halign: "right" }, 9: { halign: "right" } },
    });

    // Resumen al pie: pendiente vs vencido
    // const yFin = (doc.lastAutoTable?.finalY || 34) + 8;
    // doc.setFontSize(10);
    // doc.text(`Total pendiente: ${fmt(totalSaldo)}`, 14, yFin);
    // doc.setTextColor(200, 30, 30);
    // doc.text(`Total vencido: ${fmt(totalVencido)}`, 14, yFin + 6);
    // doc.setTextColor(0, 0, 0);
    // const alCorriente = totalSaldo - totalVencido;
    // doc.text(`Al corriente: ${fmt(alCorriente)}`, 14, yFin + 12);

    abrePDF(doc, `Cobranza-${slug(vendedorLabel)}-${slug(estadoLabel)}.pdf`);
  }

  function Excel() {
    const sheet = {
      fileName: `Cobranza-${slug(vendedorLabel)}-${slug(estadoLabel)}`,
      datas: [
        {
          sheetName: "Cobranza",
          sheetHeader: ["Vendedor", "Cliente", "Tipo", "Numero", "Fecha", "Fecha Venc.", "Dias Vencido", "Rango", "Total", "Saldo"],
          sheetData: filtradas.map((f) => ({
            v: f.vendedorNombre,
            c: f.cliente,
            t: f.tipo,
            n: f.numero,
            f: f.fecha,
            fv: f.fechaVencimiento,
            dv: f.diasVencido,
            r: f.rangoNombre,
            to: Number(f.total || 0),
            s: Number(f.saldo || 0),
          })),
          sheetFilter: ["v", "c", "t", "n", "f", "fv", "dv", "r", "to", "s"],
          columnWidths: ["12", "20", "8", "8", "10", "10", "8", "12", "10", "10"],
        },
      ],
    };
    new ExportJsonExcel(sheet).saveExcel();
  }

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_reportes || user.abonos ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button size="sm" href="/MenuVentas" className="btn btn-danger" id="botonListado">
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" color="danger" onClick={PDF}>
                  PDF <i className="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" color="primary" onClick={Excel}>
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <h3 align="center">Cobranza por Vendedor</h3>

          <Row>
            <Col md={3}>
              <Label>Vendedor</Label>
              <Input
                type="select"
                bsSize="sm"
                value={selectedVendedor}
                onChange={(e) => {
                  setSelectedVendedor(e.target.value);
                  setSelectedCliente("");
                  setCurrentPage(1);
                }}
              >
                <option value="">Todos</option>
                {vendedores.map(([id, nombre]) => (
                  <option key={id} value={id}>
                    {nombre}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label>Cliente</Label>
              <Input
                type="select"
                bsSize="sm"
                value={selectedCliente}
                onChange={(e) => {
                  setSelectedCliente(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todos</option>
                {clientesFiltro.map(([id, nombre]) => (
                  <option key={id} value={id}>
                    {nombre}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label>Estado</Label>
              <Input
                type="select"
                bsSize="sm"
                value={selectedEstado}
                onChange={(e) => {
                  setSelectedEstado(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todas</option>
                <option value="vencidas">Vencidas</option>
                <option value="noVencidas">No vencidas</option>
              </Input>
            </Col>
            <Col md={3} className="d-flex flex-column justify-content-end">
              <Search onSearch={(v) => { setSearch(v); setCurrentPage(1); }} />
            </Col>
          </Row>

          <br />
          <div>
            <b>Total pendiente:</b> {fmt(totalSaldo)} · <b>Notas:</b> {filtradas.length}
          </div>
          <div style={{ color: "#c81e1e", fontWeight: 600 }}>
            <b>Total vencido:</b> {fmt(totalVencido)} · <b>Notas vencidas:</b>{" "}
            {notasVencidas}
          </div>
          <div style={{ color: "#6c757d" }}>
            <b>Al corriente:</b> {fmt(totalSaldo - totalVencido)}
          </div>
          <br />

          <Pagination
            total={filtradas.length}
            itemsPerPage={ITEMS_PER_PAGE}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />

          <Table size="sm" striped borderless className="table-responsive-xl">
            <TableHeader headers={headers} onSorting={(field, order) => setSorting({ field, order })} />
            <tbody>
              {pagina.map((f) => (
                <tr key={f._id + f.rango}>
                  <td>{f.vendedorNombre}</td>
                  <td>{f.cliente}</td>
                  <td>{f.tipo}</td>
                  <td>{f.numero}</td>
                  <td>{f.fecha}</td>
                  <td>{f.fechaVencimiento}</td>
                  <td>{f.diasVencido}</td>
                  <td>{f.rangoNombre}</td>
                  <td>{fmt(f.total)}</td>
                  <td>{fmt(f.saldo)}</td>
                </tr>
              ))}
              <tr style={{fontWeight: "bold"}}>
                <td colSpan={7}></td>
                <td>TOTAL</td>
                <td>{fmt(totalTotal)}</td>
                <td>{fmt(totalSaldo)}</td>
              </tr>
            </tbody>
          </Table>
        </div>
      ) : null}
    </>
  );
}

export default ListadoCobranzaVendedor;
